import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input, InputGroup, InputGroupAddon, InputGroupText } from 'reactstrap';
import { ICampaignListProps, ICampaign } from '../../../interfaces/Index';
import { _ } from '../../../utils/lib';
import ButtonComponent from '../../common/Button/ButtonComponent';
import DataTable from '../../common/DataTable/DataTable';
import {
  headerData,
  footerData,
  bodyDataAndKeyTypes,
} from './CampaignTableContants';
import Toaster from 'components/common/Toaster/Toaster';
import LoaderComponent from 'components/common/Loader/Loader';
import { Can } from 'config/Can';
import './CampaignList.scss';
import { normalizeCampaignData } from 'normalizer/CampaignNormalizer';
import IconSearch from 'assets/images/search-icon.svg';
import IconClose from 'assets/images/close.svg';
export const doAPIsearch = (
  val: any,
  pageScrollPosition: any,
  fetchCampaigns: any
) => {
  if (val.length >= 1) {
    pageScrollPosition.current = 0;
    fetchCampaigns({
      criteria: val,
    });
  }
};

const CampaignList: React.FunctionComponent<ICampaignListProps> = (
  props: ICampaignListProps
) => {
  let pageScrollPosition: any = useRef(props.currentListPosition);

  const [isSearch, setIsSearching] = useState(
    props.retainedSearchText ? true : false
  );
  const [searchTerm, setSearchTerm] = useState(props.retainedSearchText);
  const [isLoadMore, setLoadMore] = useState(false);
  const [loadMoreText, setLoadMoreText] = useState({
    text: 'Load more...',
    isLoading: false,
  });
  const [campaignData, setCampaignData] = useState<ICampaign[]>([]);
  const [showToaster, setToaster] = useState({
    isVisible: false,
    message: '',
    isError: false,
  });

  useEffect(() => {
    const { fetchCampaigns, campaigns, isCampaignAddedorUpdated } = props;
    if (campaigns.length === 0 || isCampaignAddedorUpdated ) {
      fetchCampaigns();
      window.scrollTo(0, 0);
    } else {
      window.scrollTo(0, pageScrollPosition.current);
    }
  }, []);

  useEffect(() => {
    const { campaigns, pagination } = props;
    if (campaigns) {
      setCampaignData(campaigns);
      setTimeout(() => {
        window.scrollTo(
          0,
          window.pageYOffset - (window.pageYOffset - pageScrollPosition.current)
        );
      }, 250);
      if (pagination) {
        setLoadMore(true);
        setLoadMoreText({
          text: "Load more...",
          isLoading: false,
        });
      } else {
        setLoadMore(false);
      }
    }
  }, [props.campaigns]);

  useEffect(() => {
    const { error, isError } = props;
    if (isError && error) {
      alert((error! as any).errors);
    }
  }, [props.isError]);
  useEffect(() => {
    if (props.isCampaignCreated || props.isCampaignUpdated) {
      const message = props.isCampaignCreated
        ? 'Campaign created successfully!'
        : 'Campaign updated successfully';
      handleToaster(false, message);
    }
  }, [props.isCampaignCreated, props.isCampaignUpdated]);

  const handleToaster = (error: boolean, message: string) => {
    setToaster({
      isVisible: true,
      message: message,
      isError: error,
    });
  };

  const debounceSearch = useCallback(
    _.debounce(
      (value: any) =>
        doAPIsearch(value, pageScrollPosition, props.fetchCampaigns),
      1000
    ),
    []
  );

  const handleSearchInputChange = (e: any) => {
    const { fetchCampaigns } = props;
    const value = e.target.value;
    setSearchTerm(value);
    debounceSearch(value);
    if (value.length > 0 && !isSearch) {
      setIsSearching(true);
    }
    if (value.length === 0 && isSearch) {
      fetchCampaigns();
    }
  };

  const editCampaign = (e: any, i: number) => {
    const { addUpdatePageHandler } = props;

    addUpdatePageHandler(true, campaignData[i], searchTerm, window.pageYOffset);
  };

  const closeSearch = () => {
    pageScrollPosition.current = 0;
    const { fetchCampaigns } = props;
    fetchCampaigns();
    setIsSearching(false);
    setSearchTerm('');
  };

  const loadFunc = () => {
    const { fetchCampaigns, pagination } = props;
    if (pagination) {
      pageScrollPosition.current = window.pageYOffset;
      setLoadMoreText({
        text: 'Loading...',
        isLoading: true,
      });
      if (isSearch) {
        fetchCampaigns();
      }
      else {
        fetchCampaigns({
          page: pagination,
        });
      }
    }
  };
  return (
    <div className='campaign-list-container'>
      {!props.isCampaignFetched && <LoaderComponent />}
      <Toaster
        showToaster={showToaster.isVisible}
        toasterClass={
          showToaster.isError
            ? 'toaster toaster-bg-danger'
            : 'toaster toaster-bg-success'
        }
      >
        {showToaster.message || ''}
      </Toaster>
      <section className='relative container sticky-top'>
        <section className='row'>
          <section className='col-9'>
            <InputGroup>
              <InputGroupAddon addonType='prepend'>
                <InputGroupText>
                  <img
                    src={IconSearch}
                    alt='close'
                  />
                </InputGroupText>
              </InputGroupAddon>
              <Input
                className='search-input'
                placeholder='Search by campaign ID or title'
                onChange={handleSearchInputChange}
                value={searchTerm}
              />
              <div
                className={isSearch ? 'close-button' : 'hide close-button'}
                onClick={closeSearch}
              >
                <img
                  src={IconClose}
                  alt='close'
                />
              </div>
            </InputGroup>
          </section>
          <section className='col-3'>
            <Can I='create' a='campaign'>
              <ButtonComponent
                buttonText='New Campaign'
                onClickHandler={() =>
                  props.addUpdatePageHandler(
                    false,
                    null,
                    searchTerm,
                    window.pageYOffset
                  )
                }
                buttonClass='btn-primary btn btn-secondary button-large'
              />
            </Can>
          </section>
        </section>
      </section>
      <Can I='read' a='campaign'>
        <DataTable
          tableId='campaign-list'
          tableHeaderData={headerData}
          tableFooterData={footerData}
          tableBodyDataTypes={bodyDataAndKeyTypes}
          tableBodyRowData={normalizeCampaignData(campaignData)}
          // noSearchResultFlag={isSearch}
          noSearchResultFlag={true}
          editRowHandler={editCampaign}
        />
        {isLoadMore && (
          <div id='load-more-container'>
            <ButtonComponent
              buttonText={loadMoreText.text}
              buttonClass='btn-primary-outline button-medium'
              onClickHandler={loadFunc}
              isDisable={loadMoreText.isLoading}
            />
          </div>
        )}
      </Can>
    </div>
  );
};

export default CampaignList;
